/* eslint-disable no-underscore-dangle */
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Col, Modal, Row, Table, message, Tabs } from "antd";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { HomePageTable } from "../Common";

import { tableStringSorter } from "../../utils/table";
import InputElement from "./ReportInputElement";
import {
  useGetHotelById,
  useGetLMDDocumentTypes,
  useGetProjectHotelReports,
  useReportCreateReportList,
} from "../../hooks";
import {
  BIANNUALLY,
  MONTHLY,
  QUARTERLY,
  WEEKLY,
  YEARLY,
  ASNEEDED,
  DAILY,
  getAvailableDocTypes,
} from "../../utils/report";
import { getReportCopyOverSchema } from "../../schema/report.schema";

const tableHeight = window.innerHeight - 350;

const CopyOverPMSReports = ({
  open,
  setOpen,
  dataSource = [],
  hotel,
  project,
}) => {
  const [uniqueScheduleTypes, setUniqueScheduleTypes] = useState([DAILY]);

  const history = useHistory();
  const [messageApi, contextHolder] = message.useMessage();

  const { data: hotelData } = useGetHotelById(hotel, { enabled: !!hotel });

  const { data: hotelReports, refetch: retrieveHotelReports } =
    useGetProjectHotelReports(project, hotel, {
      enabled: false,
    });

  const { data: documentTypes } = useGetLMDDocumentTypes();

  const { mutate: saveReport, isLoading: isReportSaving } =
    useReportCreateReportList({
      onError: (error) => {
        message.error(error.response?.data?.message ?? "Server Error");
      },
      onSuccess: () => {
        message.success("Reports Added Successfully");
        retrieveHotelReports();
        setOpen(false);
        history.push("/report-management/view");
      },
    });

  const availableDocTypes = useMemo(
    () =>
      getAvailableDocTypes({
        hotelData: hotelData ? [hotelData] : [],
        selectedHotels: [hotel],
        documentTypes: documentTypes ?? [],
      }),
    [hotelData, hotel, documentTypes],
  );

  const reportUpdateSchema = useMemo(() => {
    return getReportCopyOverSchema(hotelReports ?? []);
  }, [hotelReports]);

  const {
    control,
    handleSubmit,
    reset,
    resetField,
    watch,
    formState: { errors },
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      report: [],
    },
    resolver: yupResolver(reportUpdateSchema),
  });
  const { remove, fields } = useFieldArray({
    control,
    name: "report",
  });

  const saveSubmit = (data) => {
    const processData = data.report.map((el) => {
      return {
        ...el,
        project_id: project,
        hotel_id: hotel,
      };
    });
    saveReport(processData);
  };

  const getIndexofRow = (recordId) => {
    const index = fields.findIndex((field) => field.id === recordId);
    return index;
  };

  const getTableColumns = (type) => {
    return [
      {
        title: "Report Name",
        dataIndex: "ReportName",
        fixed: "left",
        editable: false,
        width: "200px",
        sorter: tableStringSorter("ReportName"),
      },
      {
        title: "PMS Report Name",
        dataIndex: "PMSReportName",
        width: "250px",
        editable: false,
        sorter: tableStringSorter("PMSReportName"),
      },
      {
        title: "Report Frequency",
        dataIndex: "shedule_type",
        width: "200px",
        editable: false,
      },
      {
        title: "Report Category",
        dataIndex: "report_category",
        width: "200px",
        editable: false,
      },
      {
        title: "Signature Type",
        dataIndex: "signature_type",
        width: "200px",
        editable: true,
      },
      {
        title: "Report Order",
        dataIndex: "ReportOrder",
        width: "150px",
        editable: true,
        defaultSortOrder: "ascend",
        sorter: (a, b) =>
          (typeof a.ReportOrder !== "number" ? 99999999 : a.ReportOrder) -
          (typeof b.ReportOrder !== "number" ? 99999999 : b.ReportOrder),
      },
      {
        title: "Signature Mandatory",
        dataIndex: "SignatureMandatory",
        width: "200px",
        sorter: tableStringSorter("SignatureMandatory"),
        editable: true,
      },
      {
        title: "Smart Scan",
        dataIndex: "smart_scan_type",
        width: "200px",
        sorter: tableStringSorter("smart_scan_type"),
        editable: true,
      },
      {
        title: "Generate Redact Files",
        dataIndex: "enable_redaction",
        width: "200px",
        editable: true,
      },
      {
        title: "Generate Flat Files",
        dataIndex: "export_flat_files",
        width: "200px",
        editable: true,
      },
      {
        title: "Split Reports By Sub Header Type",
        dataIndex: "split_by_sub_header",
        width: "200px",
        editable: true,
      },
      {
        title: "Document Type",
        dataIndex: "document_type",
        width: "300px",
        editable: true,
      },
      {
        title: "Submission Method",
        dataIndex: "SourceType",
        width: "200px",
        // sorter: tableStringSorter("SourceType"),
        editable: true,
      },
      ...([MONTHLY, QUARTERLY, BIANNUALLY, YEARLY].includes(type)
        ? [
            {
              title: "Schedule Type",
              dataIndex: "schedule_trigger_type",
              width: "150px",
              editable: true,
            },
          ]
        : []),
      ...([WEEKLY, MONTHLY, QUARTERLY, YEARLY].includes(type)
        ? [
            {
              title: "Schedule",
              dataIndex: "schedule_weekday",
              key: "schedule_weekday",
              width: "150px",
              editable: true,
            },
          ]
        : []),
      ...([BIANNUALLY].includes(type)
        ? [
            {
              title: "Schedule",
              dataIndex: "schedule_trigger_data",
              key: "schedule_trigger_data",
              width: "150px",
              editable: true,
            },
          ]
        : []),
      ...([MONTHLY, QUARTERLY, YEARLY].includes(type)
        ? [
            {
              title: "Month",
              dataIndex: "schedule_months",
              key: "schedule_months",
              width: "150px",
              editable: true,
              sorter: tableStringSorter("schedule_months"),
            },
          ]
        : []),
      {
        title: "myDocs Report Type",
        dataIndex: "mydocs_report_type",
        width: "200px",
        // sorter: tableStringSorter("report_type"),
        editable: false,
      },
      {
        title: "Business Date",
        dataIndex: "business_date",
        width: "200px",
        editable: true,
      },
      {
        title: "Subjects",
        dataIndex: "emailSubjects",
        width: "200px",
        editable: true,
        sorter: tableStringSorter("emailSubjects"),
      },
      {
        title: "File Names",
        dataIndex: "filenames",
        width: "200px",
        sorter: tableStringSorter("filenames"),
        editable: false,
      },
      {
        title: "File Extension",
        dataIndex: "FileExtension",
        width: "150px",
        sorter: tableStringSorter("FileExtension"),
        editable: true,
      },
      {
        title: "File Mapping Name",
        dataIndex: "FileMappingName",
        width: "200px",
        sorter: tableStringSorter("FileMappingName"),
        editable: true,
        // ...this.getColumnSearchProps("FileMappingName", type),
      },
      {
        title: "Compliance",
        dataIndex: "ComplianceEnabled",
        width: "200px",
        sorter: tableStringSorter("ComplianceEnabled"),
        editable: true,
      },
      {
        title: "Report Name Matching Condition",
        dataIndex: "report_name_matching_condition",
        width: "200px",
        sorter: tableStringSorter("report_name_matching_condition"),
        editable: true,
      },
      {
        title: "File Extension Mapping",
        dataIndex: "file_extension_mapping",
        width: "200px",
        sorter: tableStringSorter("file_extension_mapping"),
        editable: true,
      },
      {
        title: "Activation Date",
        dataIndex: "activation_date",
        width: "200px",
        sorter: tableStringSorter("activation_date"),
        editable: true,
      },
      {
        title: "Deactivation Date",
        dataIndex: "deactivation_date",
        width: "200px",
        sorter: tableStringSorter("deactivation_date"),
        editable: true,
      },
      {
        title: "Signature Activation Date",
        dataIndex: "signature_activation_date",
        width: "200px",
        editable: true,
      },
      {
        title: "Signature Deactivation Date",
        dataIndex: "signature_deactivation_date",
        width: "200px",
        editable: true,
      },
      {
        title: "Operation",
        key: "operation",
        width: "180px",
        fixed: "right",
        render: (item, record, index) => {
          return (
            <Row justify="space-between">
              <Col span={6}>
                <Button
                  onClick={() => remove(getIndexofRow(record.id))}
                  type="link"
                >
                  Remove
                </Button>
              </Col>
            </Row>
          );
        },
      },
    ];
  };

  const mergedColumns = (type) =>
    getTableColumns(type).map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        render: (_, record, index) => (
          <Controller
            control={control}
            name={`report.${getIndexofRow(record.id)}.${col.dataIndex}`}
            render={({ field }) => (
              <InputElement
                field={field}
                resetField={resetField}
                watch={watch}
                setValue={setValue}
                getValues={getValues}
                reportCategories={[]}
                errors={
                  errors?.report ? errors.report[getIndexofRow(record.id)] : ""
                }
                dataIndex={col.dataIndex}
                documentTypes={availableDocTypes}
                formInputPath={`report.${getIndexofRow(record.id)}.`}
              />
            )}
          />
        ),
      };
    });

  const resetForm = () => {
    const resetValues = [];
    dataSource.forEach((row) => {
      resetValues.push({
        PMSReportName: row.report_name,
        ReportName: row.mydocs_report_name,
        report_category: [],
        shedule_type: row.shedule_type,
        hotel_id: hotel,
        signature_type: row.signature_type,
        SourceType: row.source_type,
        filenames: row.file_name ? [row.file_name] : [],
        business_date: row.business_date,
        ComplianceEnabled: row.shedule_type !== ASNEEDED,
        mydocs_report_type: row.mydocs_report_type,
        FileMappingName: row.mydocs_report_name,
        smart_scan_type: !row.mydocs_report_type
          ? null
          : row.mydocs_report_type === "System"
          ? 0
          : 1,
        enable_redaction: false,
        export_flat_files: false,
        document_type: [],
        split_by_sub_header: undefined,
        report_name_matching_condition: row.report_name_matching_condition,
      });
    });
    reset({ report: resetValues });
  };

  useEffect(() => {
    if (Array.isArray(dataSource)) {
      resetForm();
      const uniqueSchedule = new Set();

      dataSource.forEach((row) => {
        uniqueSchedule.add(row.shedule_type);
      });
      setUniqueScheduleTypes(Array.from(uniqueSchedule));
      retrieveHotelReports();
    }
  }, [dataSource]);

  const onError = (error) => {
    const reportNameExistArray = [];
    error.report.forEach((report, index) => {
      if (report?.ReportName?.type === "is-report-name-already-exist") {
        const reportName = fields[index].ReportName;
        reportNameExistArray.push(reportName);
      }
    });

    if (reportNameExistArray?.length) {
      messageApi.open({
        type: "error",
        content: `${reportNameExistArray.join(
          ", ",
        )} Report name(s) already exists`,
        style: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "fixed",
          left: "50%",
          transform: "translate(-50%, -10%)",
          maxWidth: "80%",
        },
      });
    } else message.error("Please fix all the input errors");
  };

  return (
    <>
      {contextHolder}
      <Modal
        open={open}
        footer={null}
        style={{ top: 20 }}
        width={1600}
        onCancel={() => setOpen(false)}
      >
        <Tabs
          defaultActiveKey="1"
          items={uniqueScheduleTypes.map((type) => ({
            key: type,
            label: type,
            children: (
              <HomePageTable>
                <form onSubmit={handleSubmit(saveSubmit, onError)}>
                  <Table
                    columns={mergedColumns(type)}
                    dataSource={
                      fields.filter((field) => field.shedule_type === type) ??
                      []
                    }
                    rowKey="id"
                    pagination={false}
                    scroll={{ x: 800, y: tableHeight }}
                  />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginTop: "20px",
                    }}
                  >
                    <Button
                      type="primary"
                      htmlType="submit"
                      style={{ marginRight: "10px" }}
                      loading={isReportSaving}
                      disabled={!fields.length}
                    >
                      Save
                    </Button>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                  </div>
                </form>
              </HomePageTable>
            ),
          }))}
        />
      </Modal>
    </>
  );
};

export default CopyOverPMSReports;
